import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/context/AuthContext';
import { useLang } from '@/context/LangContext';
import LangSwitcher from '@/components/LangSwitcher';
import GoogleAuthButton from '@/components/GoogleAuthButton';

const TEXTURE = '/garde-valley.jpg';

export default function SignIn() {
    const { t } = useLang();
    const { signIn } = useAuth();
    const navigate = useNavigate();
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setError(null); setLoading(true);
        try {
            const { error } = await signIn({ email, password });
            setLoading(false);
            if (error) {
                const raw = (error.message || '').toString();
                // Supabase-js sometimes leaks fetch internals; normalize common cases
                if (/body stream already read/i.test(raw) || /failed to fetch/i.test(raw)) {
                    setError(t('auth.error'));
                } else if (/invalid login credentials|invalid_grant/i.test(raw)) {
                    setError(t('auth.error'));
                } else {
                    setError(raw || t('auth.error'));
                }
                return;
            }
            navigate('/app/dashboard');
        } catch (err) {
            setLoading(false);
            setError(t('auth.error'));
        }
    };

    return (
        <div className="min-h-screen grid lg:grid-cols-2 bg-background">
            {/* LEFT — Visual showcase with framed portrait + atmosphere */}
            <div
                className="hidden lg:flex relative overflow-hidden items-center justify-center bg-[#1a1612]"
                data-testid="signin-visual"
            >
                {/* Ambient gradient */}
                <div
                    className="absolute inset-0 opacity-90"
                    style={{
                        backgroundImage:
                            'radial-gradient(80% 60% at 50% 0%, rgba(212,114,47,0.18) 0%, rgba(26,22,18,0) 65%), ' +
                            'radial-gradient(70% 60% at 50% 100%, rgba(57,99,76,0.22) 0%, rgba(26,22,18,0) 65%), ' +
                            'linear-gradient(180deg, #1f1a16 0%, #14110e 100%)',
                    }}
                />
                {/* Grain overlay for premium texture */}
                <div
                    className="absolute inset-0 mix-blend-overlay opacity-[0.18] pointer-events-none"
                    style={{
                        backgroundImage:
                            "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='160' height='160'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/></filter><rect width='100%' height='100%' filter='url(%23n)' opacity='0.6'/></svg>\")",
                    }}
                />
                {/* Floating accents */}
                <div className="absolute -top-24 -left-24 h-72 w-72 rounded-full bg-primary/20 blur-3xl" />
                <div className="absolute -bottom-32 -right-20 h-80 w-80 rounded-full bg-secondary/20 blur-3xl" />

                {/* Return link */}
                <Link
                    to="/"
                    className="absolute top-6 left-6 z-20 inline-flex items-center gap-2 text-background/80 hover:text-background text-sm transition-colors"
                    data-testid="auth-brand"
                >
                    ← Retour à l'accueil
                </Link>

                {/* Framed image — fully visible (contain), not cropped — enlarged ≈ +2cm */}
                <div className="relative z-10 flex flex-col items-center gap-7 px-8 py-10 max-w-[620px]">
                    <div className="relative w-full">
                        <div className="absolute -inset-5 bg-gradient-to-br from-primary/35 via-secondary/25 to-transparent rounded-[32px] blur-2xl opacity-80 animate-pulse-slow" />
                        <div className="relative rounded-2xl overflow-hidden ring-1 ring-background/15 shadow-2xl transition-transform duration-700 hover:scale-[1.01]">
                            <img
                                src={TEXTURE}
                                alt="African Valley — Pilotage projets 360°"
                                className="w-full h-auto object-contain block select-none"
                                draggable={false}
                            />
                            {/* Subtle bottom legibility gradient inside the frame */}
                            <div className="absolute inset-x-0 bottom-0 h-24 pointer-events-none bg-gradient-to-t from-black/40 to-transparent" />
                        </div>
                    </div>
                    <div className="text-center text-background/85 max-w-md">
                        <div className="eyebrow text-primary/90 mb-2">Plateforme africaine 360°</div>
                        <h2 className="font-heading text-2xl leading-snug">
                            Le pilotage des projets <span className="text-primary">prend racine</span>.
                        </h2>
                        <p className="text-sm text-background/65 mt-3 leading-relaxed">
                            Gouvernance, finance, GED, IA et bailleurs — réunis dans un même espace souverain.
                        </p>
                    </div>
                </div>
            </div>

            {/* RIGHT — Auth form (unchanged) */}
            <div className="flex flex-col">
                <div className="flex justify-between items-center px-8 lg:px-16 py-6">
                    <Link to="/" className="lg:hidden inline-flex items-center gap-2.5">
                        <img src="/brand/african-valley-logo-256.png" alt="African Valley" className="h-9 w-9 object-contain rounded" />
                        <span className="font-heading text-xl">African <span className="text-primary">Valley</span></span>
                    </Link>
                    <Link to="/" className="hidden lg:inline-flex items-center gap-2.5">
                        <img src="/brand/african-valley-logo-256.png" alt="African Valley" className="h-9 w-9 object-contain rounded" />
                        <div className="leading-tight">
                            <div className="font-heading text-lg">African <span className="text-primary">Valley</span></div>
                            <div className="text-[9px] uppercase tracking-[0.22em] text-muted-foreground">Projet 360°</div>
                        </div>
                    </Link>
                    <div className="ml-auto"><LangSwitcher /></div>
                </div>
                <div className="flex-1 flex items-center px-8 lg:px-16 pb-12">
                    <div className="w-full max-w-md mx-auto">
                        <div className="eyebrow mb-3">Connexion</div>
                        <h1 className="font-heading text-4xl mb-8">{t('auth.signin_title')}</h1>

                        <GoogleAuthButton testid="signin-google-btn" />

                        <div className="my-6 flex items-center gap-3 text-[11px] uppercase tracking-[0.18em] text-foreground/45">
                            <div className="flex-1 h-px bg-border" />
                            <span>ou avec votre email</span>
                            <div className="flex-1 h-px bg-border" />
                        </div>

                        <form onSubmit={submit} className="space-y-5" data-testid="signin-form">
                            <div className="space-y-2">
                                <Label htmlFor="email">{t('auth.email')}</Label>
                                <Input
                                    id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                                    className="h-12" placeholder="vous@organisation.com" data-testid="signin-email"
                                />
                            </div>
                            <div className="space-y-2">
                                <div className="flex items-center justify-between">
                                    <Label htmlFor="password">{t('auth.password')}</Label>
                                    <Link
                                        to="/auth/reset"
                                        className="text-xs text-primary hover:underline"
                                        data-testid="link-forgot-password"
                                    >
                                        Mot de passe oublié&nbsp;?
                                    </Link>
                                </div>
                                <Input
                                    id="password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                                    className="h-12" data-testid="signin-password"
                                />
                            </div>
                            {error && (
                                <div className="text-sm text-destructive bg-destructive/10 border border-destructive/30 rounded-md px-3 py-2" data-testid="signin-error">
                                    {error}
                                </div>
                            )}
                            <Button
                                type="submit" disabled={loading}
                                className="w-full h-12 bg-primary hover:bg-primary-hover text-primary-foreground"
                                data-testid="signin-submit"
                            >
                                {loading ? <Loader2 className="animate-spin" size={16} /> : t('auth.submit_signin')}
                            </Button>
                        </form>

                        <div className="mt-8 text-center">
                            <Link to="/auth/signup" className="text-sm text-foreground/70 hover:text-primary transition-colors" data-testid="link-to-signup">
                                {t('auth.switch_to_signup')}
                            </Link>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
